import { SECTION_BODY_GUTTER } from '../../nav/PageContainer';
import { enterTenant } from '../../components/ActingBanner';
import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { API_BASE } from '../../client/config';
import { ConfirmModal, ModalShell } from '../../components/menu';
import { SearchInput, Button } from '../../components/ui';
import { describeApiError, toast } from '../../components/toast';
import { PageHeader } from '../../nav/Breadcrumb';
import { GroupMemberEditor, type GroupMemberAssignments, type GroupMemberOptions } from '../../components/GroupMemberEditor';
import { AdminGroupSection, adminAccessFields } from '../../components/AdminGroupSection';
import { OrgPlayerServerCard } from '../../components/OrgPlayerServerCard';
import { OrgAiConfigCard } from '../../components/OrgAiConfigCard';
import { ROLE_OPTIONS, describeGroupScope } from '../../labels/orgConcepts';
import { useWriteGate } from '../../nav/useWriteGate';
import {
  OrgAdminListSkeleton,
  OrgAdminDetailSkeleton,
} from '../../components/skeleton/layouts/OrgAdminSkeleton';

// =============================================================================
// H9 > 고객사 관리(운영사) / 조직 관리 > 조직 정보·관리자(고객, scope='self').
//   OrgAdminList   /h9/tenants          — 고객사 목록(행 클릭 → 상세)
//   OrgAdminDetail /h9/tenants/:orgId · /org/admins(self) — 조직 정보 카드 + 관리자 테이블
//                                      (관리자 추가/수정 팝업, 비밀번호 초기화)
// =============================================================================

type OwnerReadiness = {
  orgId: string; ready: boolean; ownerCount: number; usableOwnerCount: number;
  reasons: Array<'owner_missing' | 'owner_account_missing' | 'owner_login_missing'>;
};
type OwnerCandidate = { userId: string; email: string; name: string | null; loginId: string | null; canOwn: boolean };
const OWNER_ISSUES = {
  owner_missing: 'Owner 계정이 지정되지 않았습니다.',
  owner_account_missing: 'Owner로 지정된 계정을 찾을 수 없습니다.',
  owner_login_missing: 'Owner 계정에 로그인 수단이 등록되지 않았습니다.',
};

type OrgSummary = {
  id: string;
  name: string;
  status: 'active' | 'suspended';
  orgRole: 'admin' | 'super';
  isBoOrg: boolean;
  adminCount: number;
  deviceCount: number;
  lastSeenAt: string | null;
  adminLoginIds: string[];
  createdAt: string;
  deletedAt: string | null;
  /** 완전 삭제 예정 시각(서버 계산 — ORG_RETENTION_DAYS 반영). */
  purgeAt: string | null;
  ownerReadiness?: OwnerReadiness;
};

export type AdminRow = {
  userId: string;
  name: string | null;
  loginId: string | null;
  phone: string | null;
  email: string;
  role: string;
  lastSeenAt: string | null;
};

/** 관리자 저장 결과 — 서버가 한 트랜잭션으로 저장한 뒤 돌려주는 값.
 *  메일·인앱 알림은 저장이 끝난 뒤 실행되므로 실패해도 저장은 유효하다. */
export type AdminSaveResult = {
  userId?: string;
  role?: string;
  central?: boolean;
  assignments?: Array<{ groupId: string; role: string }>;
  delivery?: {
    email?: { status: 'sent' | 'failed' | 'not_requested' | 'unverified'; detail?: string };
    notification?: { status: 'sent' | 'failed' | 'not_requested' | 'unverified'; detail?: string };
  };
};

/**
 * 저장은 됐지만 안내 메일·알림이 못 나간 경우를 성공 토스트로 덮지 않는다(R22).
 * 예전에는 메일 발송 실패를 서버 로그에만 남기고 화면은 늘 '추가되었습니다'를 띄워,
 * 운영자가 "메일 확인하세요"라고 안내한 뒤에야 메일이 안 갔다는 걸 알았다.
 */
export function reportAdminSaveResult(result: AdminSaveResult, successMessage: string): void {
  const failed = [
    result.delivery?.email?.status === 'failed' ? result.delivery.email.detail ?? '안내 메일을 보내지 못했습니다.' : null,
    result.delivery?.notification?.status === 'failed' ? result.delivery.notification.detail ?? '인앱 알림을 남기지 못했습니다.' : null,
  ].filter((message): message is string => message !== null);
  if (failed.length === 0) { toast(successMessage, 'success'); return; }
  toast(`${successMessage.replace(/\.$/, '')} 다만 ${failed.join(' ')} 비밀번호는 직접 전달해 주세요.`, 'error');
}

type TenantDetail = {
  org: {
    id: string;
    name: string;
    orgRole: 'admin' | 'super';
    createdAt: string;
    deletedAt: string | null;
    purgeAt: string | null;
  };
  deviceCount: number;
  isBoOrg: boolean;
  admins: AdminRow[];
  ownerReadiness?: OwnerReadiness;
};

const PAGE_SIZE = 10;

/** 완전 삭제까지 남은 일수(D-day) — 서버가 계산한 purgeAt(ISO) 기준.
 *  ORG_RETENTION_DAYS가 바뀌어도 UI와 실제 purge 시점이 일치한다. */
function purgeDday(purgeAt: string | null): number | null {
  if (!purgeAt) return null;
  return Math.max(0, Math.ceil((new Date(purgeAt).getTime() - Date.now()) / 86_400_000));
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}. ${p(d.getMonth() + 1)}. ${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}. ${p(d.getMonth() + 1)}. ${p(d.getDate())}`;
}

// -----------------------------------------------------------------------------
// 목록
// -----------------------------------------------------------------------------
export function OrgAdminList(): JSX.Element {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState<OrgSummary[]>([]);
  const [query, setQuery] = useState('');
  const [orgFilter, setOrgFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState<'' | 'admin' | 'super'>('');
  const [page, setPage] = useState(1);
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<OrgSummary | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<OrgSummary | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(() => {
    setIsLoading(true);
    fetch(`${API_BASE}/api/bo/tenants`)
      .then((r) => (r.ok ? (r.json() as Promise<OrgSummary[]>) : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(setOrgs)
      .catch((e: unknown) => setErr(describeApiError(e)))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = orgs.filter((o) => {
      if (orgFilter && o.id !== orgFilter) return false;
      // BRICK-169/173 — 권한은 org_role 그대로 본다. isBoOrg 는 "BO 운영자가
      // 소속됐는가"까지 섞인 파생값이라, 권한을 admin 으로 바꿔도 필터·라벨이
      // 최고 관리자로 남았다. 그 값은 삭제 보호 판정에만 쓴다.
      if (roleFilter === 'super' && o.orgRole !== 'super') return false;
      if (roleFilter === 'admin' && o.orgRole === 'super') return false;
      if (
        q &&
        !o.name.toLowerCase().includes(q) &&
        !o.adminLoginIds.some((id) => id.toLowerCase().includes(q))
      )
        return false;
      return true;
    });
    // 삭제 대기(deletedAt) 조직은 항상 테이블 최하단으로 — 활성 조직 우선 노출.
    return list.sort((a, b) => Number(!!a.deletedAt) - Number(!!b.deletedAt));
  }, [orgs, query, orgFilter, roleFilter]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const rows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const selectCls = 'w-[180px] px-2.5 py-2 bg-panel-2 border border-line rounded text-[14px] text-text';

  if (isLoading) {
    return <OrgAdminListSkeleton />;
  }

  return (
    <div className="relative h-full overflow-auto p-4 md:p-6">
      <PageHeader items={[{ label: '고객사 관리' }]} />

      {/* 필터 — 조직 / 권한 */}
      <div className="flex items-end gap-4 mb-4 max-md:grid max-md:grid-cols-2 max-md:gap-2 max-md:[&_select]:w-full">
        <label className="flex flex-col gap-1">
          <span className="text-[12px] text-text-dim">조직</span>
          <select
            value={orgFilter}
            onChange={(e) => {
              setOrgFilter(e.target.value);
              setPage(1);
            }}
            className={selectCls}
          >
            <option value="">전체</option>
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[12px] text-text-dim">권한</span>
          <select
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value as '' | 'admin' | 'super');
              setPage(1);
            }}
            className={selectCls}
          >
            <option value="">전체</option>
            <option value="admin">관리자</option>
            <option value="super">최고 관리자</option>
          </select>
        </label>
      </div>

      <div className="flex items-center justify-between mb-3 max-md:flex-wrap max-md:gap-2">
        <span className="whitespace-nowrap text-[14px] text-text-dim">총 {filtered.length}건</span>
        <div className="flex items-center gap-2 max-md:contents [&_button]:whitespace-nowrap">
          <Button variant="primary" onClick={() => setAddOpen(true)}>+ 조직 추가</Button>
          <label className="contents"><span className="sr-only">조직명, 아이디 검색</span>
            <SearchInput
              value={query}
              onChange={(v) => {
                setQuery(v);
                setPage(1);
              }}
              placeholder="조직명, 아이디 검색"
              className="w-[240px] max-md:order-last max-md:w-full"
            />
          </label>
        </div>
      </div>
      {err && <p className="font-mono text-[12px] text-red mb-3">{err}</p>}
      <div className="border border-line rounded-md overflow-x-auto">
        <table className="w-full text-[14px]">
          <thead>
            <tr className="bg-panel-2 text-text-dim text-left">
              <th className="px-3 py-2.5 font-medium">조직명</th>
              <th className="px-3 py-2.5 font-medium">권한</th>
              <th className="px-3 py-2.5 font-medium">등록된 관리자 수</th>
              <th className="px-3 py-2.5 font-medium">등록된 디바이스 수</th>
              <th className="px-3 py-2.5 font-medium">조직 등록일</th>
              <th className="px-3 py-2.5 font-medium">마지막 접속일</th>
              <th className="px-3 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {rows.map((o) => (
              <tr key={o.id} className="border-t border-line hover:bg-panel-hover">
                <td className="px-3 py-2.5 max-w-[280px]">
                  <div className="flex items-center gap-2 min-w-0">
                    <button
                      type="button"
                      onClick={() => navigate(o.id)}
                      className="text-accent hover:underline truncate"
                      title={o.name}
                    >
                      {o.name}
                    </button>
                    {o.deletedAt && (
                      <span className="shrink-0 rounded-sm bg-red/10 px-1.5 py-0.5 text-[11px] font-medium text-red border border-red/30">
                        삭제 예정 (D-{purgeDday(o.purgeAt)})
                      </span>
                    )}
                  </div>
                  {o.ownerReadiness && !o.ownerReadiness.ready && <p className="mt-1 text-[12px] text-red">
                    Owner 준비 필요: {o.ownerReadiness.reasons.map(reason => OWNER_ISSUES[reason]).join(' ')}
                  </p>}
                </td>
                <td className="px-3 py-2.5">{o.orgRole === 'super' ? '최고 관리자' : '관리자'}</td>
                <td className="px-3 py-2.5">{o.adminCount.toLocaleString()}</td>
                <td className="px-3 py-2.5">{o.deviceCount.toLocaleString()}</td>
                <td className="px-3 py-2.5">{formatDate(o.createdAt)}</td>
                <td className="px-3 py-2.5">{formatDate(o.lastSeenAt)}</td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                  {/* 삭제 대기 조직은 목록에서 액션 미노출 — 복구·즉시삭제는 조직 상세에서. */}
                  {!o.deletedAt && (
                    <>
                      {(
                        <button
                          type="button"
                          onClick={() => { void enterTenant(o.id, 'read').catch((e: unknown) => toast(describeApiError(e), 'error')); }}
                          title="이 고객사의 화면을 읽기 전용으로 봅니다"
                          className="inline-flex items-center px-3 py-1.5 mr-1.5 text-[13px] border border-line rounded hover:border-line-strong hover:bg-panel"
                        >
                          보기
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setEditTarget(o)}
                        className="inline-flex items-center px-3 py-1.5 mr-1.5 text-[13px] border border-line rounded hover:border-line-strong hover:bg-panel"
                      >
                        수정
                      </button>
                      <button
                        type="button"
                        disabled={o.isBoOrg}
                        onClick={() => setDeleteTarget(o)}
                        className="inline-flex items-center px-3 py-1.5 text-[13px] border border-line rounded hover:border-line-strong hover:bg-panel disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        삭제
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-10 text-center text-text-dim">
                  조회된 조직이 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <div className="flex justify-end items-center gap-1 mt-3">
          {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
            <button
              key={n}
              type="button"
              disabled={n === safePage}
              onClick={() => setPage(n)}
              className={`min-w-[28px] h-[28px] px-1 text-[13px] rounded border ${
                n === safePage
                  ? 'border-accent text-accent font-semibold'
                  : 'border-line text-text-dim hover:text-text hover:border-line-strong'
              }`}
            >
              {n}
            </button>
          ))}
        </div>
      )}

      {addOpen && (
        <OrgFormModal
          mode="add"
          onClose={() => setAddOpen(false)}
          onSaved={() => {
            setAddOpen(false);
            toast('추가되었습니다.', 'success');
            load();
          }}
        />
      )}
      {editTarget && (
        <OrgFormModal
          mode="edit"
          org={{ id: editTarget.id, name: editTarget.name, orgRole: editTarget.orgRole }}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            toast('저장되었습니다.', 'success');
            load();
          }}
        />
      )}
      {deleteTarget && (
        <ConfirmModal
          title="알림"
          message={'삭제 후 7일간 보관되며, 그 전까지 복구할 수 있습니다.\n7일이 지나면 조직·디바이스·미디어·관리자 계정이 완전히 삭제됩니다.\n삭제하시겠습니까?'}
          confirmLabel="확인"
          danger={false}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={async () => {
            const r = await fetch(`${API_BASE}/api/bo/tenants/${encodeURIComponent(deleteTarget.id)}`, {
              method: 'DELETE',
            });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            toast('삭제 예정으로 처리되었습니다. (7일 보관)', 'success');
            load();
          }}
        />
      )}
    </div>
  );
}

// 조직 추가/수정 팝업(Figma Popup_조직 추가 / Popup_조직 수정) —
// 조직명* + 권한 라디오(관리자/최고 관리자). 중복 조직명이면 인라인 에러.
function OrgFormModal({
  mode,
  scope = 'bo',
  org,
  onClose,
  onSaved,
}: {
  mode: 'add' | 'edit';
  /** self = FO 자기 조직 — 권한 라디오 숨김(자가 승격 차단), /orgs/current PATCH. */
  scope?: 'bo' | 'self';
  org?: { id: string; name: string; orgRole: 'admin' | 'super' };
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(org?.name ?? '');
  const [role, setRole] = useState<'admin' | 'super'>(org?.orgRole ?? 'admin');
  const [busy, setBusy] = useState(false);
  const [nameErr, setNameErr] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [ownerEmail, setOwnerEmail] = useState('');
  const [owner, setOwner] = useState<OwnerCandidate | null>(null);
  const [ownerConfirmed, setOwnerConfirmed] = useState(false);
  const [ownerChecking, setOwnerChecking] = useState(false);
  const [ownerError, setOwnerError] = useState<string | null>(null);
  const ownerRequest = useRef<AbortController | null>(null);
  const ids = useId();
  useEffect(() => () => ownerRequest.current?.abort(), []);
  const ownerReady = mode !== 'add' || (owner?.canOwn === true && ownerConfirmed && !ownerChecking);

  const checkOwner = async () => {
    ownerRequest.current?.abort();
    const controller = new AbortController(); ownerRequest.current = controller;
    setOwner(null); setOwnerConfirmed(false); setOwnerChecking(true); setOwnerError(null);
    try {
      const response = await fetch(`${API_BASE}/api/bo/owner-candidate?email=${encodeURIComponent(ownerEmail.trim())}`, {
        credentials: 'include', signal: controller.signal,
      });
      if (!response.ok) throw new Error(response.status === 404
        ? '기존 계정을 찾을 수 없습니다. 로그인 계정의 이메일을 확인해 주세요.' : 'Owner 계정을 확인하지 못했습니다.');
      const candidate = await response.json() as OwnerCandidate;
      if (controller.signal.aborted) return;
      setOwner(candidate);
      if (!candidate.canOwn) setOwnerError(OWNER_ISSUES.owner_login_missing);
    } catch (reason) {
      if (!controller.signal.aborted) setOwnerError(reason instanceof Error ? reason.message : 'Owner 계정을 확인하지 못했습니다.');
    } finally { if (!controller.signal.aborted) setOwnerChecking(false); }
  };

  const submit = async () => {
    if (!name.trim() || busy || !ownerReady) return;
    setBusy(true);
    setErr(null);
    setNameErr(null);
    try {
      const r =
        mode === 'add'
          ? await fetch(`${API_BASE}/api/bo/tenants`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ name: name.trim(), orgRole: role, ownerUserId: owner!.userId, confirmOwner: ownerConfirmed }),
            })
          : scope === 'self'
            ? await fetch(`${API_BASE}/api/orgs/current`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: name.trim() }),
              })
            : await fetch(`${API_BASE}/api/bo/tenants/${encodeURIComponent(org!.id)}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: name.trim(), orgRole: role }),
              });
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { error?: string };
        if (body.error === 'duplicate_org_name') {
          setNameErr('중복된 조직명입니다.');
        } else if (body.error?.startsWith('owner_')) {
          setOwner(null); setOwnerConfirmed(false);
          setOwnerError(body.error === 'owner_login_missing' ? OWNER_ISSUES.owner_login_missing : 'Owner 계정을 다시 확인하고 연결을 선택해 주세요.');
        } else {
          setErr(describeApiError(new Error(`HTTP ${r.status}`)));
        }
        setBusy(false);
        return;
      }
      onSaved();
    } catch (e) {
      setErr(describeApiError(e));
      setBusy(false);
    }
  };

  // 제출·취소는 ModalShell의 고정 푸터로(검수 S3) — 본문이 길어져도 밀려 나가지 않는다.
  return (
    <ModalShell title={mode === 'add' ? '조직 추가' : '조직 수정'} onClose={onClose} size="md"
      footer={<>
        <Button onClick={onClose} disabled={busy}>취소</Button>
        <Button variant="primary" onClick={() => void submit()} disabled={!name.trim() || busy || !ownerReady}>
          {busy ? '처리 중…' : mode === 'add' ? '추가' : '저장'}
        </Button>
      </>}>
      <div className="flex flex-col gap-4">
        <div>
          <label className="text-[13px] font-semibold text-text" htmlFor={`${ids}-org-name`}>조직명 <span className="text-red">*</span></label>
          <input
            id={`${ids}-org-name`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="조직명 입력"
            autoFocus
            className={`w-full px-3 py-2 mt-1.5 bg-panel-2 border rounded text-[14px] text-text ${nameErr ? 'border-red' : 'border-line'}`}
          />
          {nameErr && <p className="text-[12px] text-red mt-1">{nameErr}</p>}
        </div>
        {mode === 'add' && <div className="space-y-2 rounded border border-line p-3">
          <label className="block text-[13px] font-semibold text-text" htmlFor="new-org-owner-email">첫 Owner 계정 이메일 <span className="text-red">*</span></label>
          <div className="flex gap-2">
            <input id="new-org-owner-email" type="email" value={ownerEmail} disabled={busy}
              onChange={(event) => {
                ownerRequest.current?.abort(); setOwnerChecking(false);
                setOwnerEmail(event.target.value); setOwner(null); setOwnerConfirmed(false); setOwnerError(null);
              }} placeholder="로그인 가능한 기존 계정 이메일"
              className="min-w-0 flex-1 rounded border border-line bg-panel-2 px-3 py-2 text-[14px]" />
            <Button disabled={busy || ownerChecking || !ownerEmail.trim()} onClick={() => void checkOwner()}>
              {ownerChecking ? '확인 중…' : '계정 확인'}
            </Button>
          </div>
          <p className="text-[12px] text-text-dim">기존 로그인 계정을 연결합니다. 계정 확인·연결만으로 초대 메일을 보내지 않습니다.</p>
          {owner && <div className="text-[13px] text-text">
            <p>{owner.name ?? owner.email} · {owner.email}{owner.loginId ? ` · ${owner.loginId}` : ''}</p>
            <label className="mt-2 flex items-start gap-2">
              <input type="checkbox" checked={ownerConfirmed} disabled={busy || !owner.canOwn}
                onChange={(event) => setOwnerConfirmed(event.target.checked)} />
              이 계정을 새 조직의 Owner로 지정합니다. 기존 조직의 권한과 로그인 정보는 유지됩니다.
            </label>
          </div>}
          {ownerError && <p role="alert" className="text-[12px] text-red">{ownerError}</p>}
        </div>}
        {scope === 'bo' && (
        <div>
          <span className="text-[13px] font-semibold text-text">권한 <span className="text-red">*</span></span>
          <div className="flex items-center gap-6 mt-2">
            {([['admin', '관리자'], ['super', '최고 관리자']] as const).map(([v, label]) => (
              <label key={v} className="flex items-center gap-1.5 text-[14px] text-text cursor-pointer">
                <input
                  type="radio"
                  name="org-role"
                  className="accent-accent w-4 h-4"
                  checked={role === v}
                  onChange={() => setRole(v)}
                />
                {label}
              </label>
            ))}
          </div>
        </div>
        )}
        {err && <p className="font-mono text-[12px] text-red">{err}</p>}
      </div>
    </ModalShell>
  );
}

// -----------------------------------------------------------------------------
// 상세
// -----------------------------------------------------------------------------
export function OrgAdminDetail({ scope = 'bo' }: { scope?: 'bo' | 'self' }): JSX.Element {
  const { orgId = '' } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  // FO 최고 관리자(self)는 자기 조직 전용 엔드포인트 — BO 가드를 타지 않는다.
  const detailUrl =
    scope === 'self'
      ? `${API_BASE}/api/orgs/current/detail`
      : `${API_BASE}/api/bo/tenants/${encodeURIComponent(orgId)}/detail`;
  const adminsBase =
    scope === 'self'
      ? `${API_BASE}/api/orgs/current/admins`
      : `${API_BASE}/api/bo/tenants/${encodeURIComponent(orgId)}/admins`;
  const [detail, setDetail] = useState<TenantDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AdminRow | null>(null);
  const [deleteAdminTarget, setDeleteAdminTarget] = useState<AdminRow | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [groupTarget, setGroupTarget] = useState<AdminRow | null>(null);
  /** 관리자 추가 모달의 담당 그룹. 계정 생성과 같은 요청에 실어 보낸다 — 예전에는 이 API가
   *  assignments 를 버려서 '계정을 만든 뒤 목록을 다시 읽어 배정을 따로 저장'하는 두 단계였고,
   *  중간에 끊기면 계정만 생기고 담당 그룹이 비었다(2026-09-17 대교 ②). */
  const [newAdminGroups, setNewAdminGroups] = useState<Set<string>>(new Set());
  /** 중앙 관리자(모든 그룹 관리) 지정 — 권한이 '그룹 관리자'일 때만 뜻이 있다. */
  const [newAdminCentral, setNewAdminCentral] = useState(false);
  const [groupData, setGroupData] = useState<(GroupMemberOptions & { orgId: string; members: GroupMemberAssignments[] }) | null>(null);
  const [groupError, setGroupError] = useState<string | null>(null);
  const [groupLoading, setGroupLoading] = useState(true);
  const [groupRevision, setGroupRevision] = useState(0);

  const load = useCallback(() => {
    setIsLoading(true);
    fetch(detailUrl)
      .then((r) => (r.ok ? (r.json() as Promise<TenantDetail>) : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => {
        setDetail(d);
        setErr(null);
      })
      .catch((e: unknown) => setErr(describeApiError(e)))
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, scope]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setGroupTarget(null);
  }, [detailUrl]);

  useEffect(() => {
    const controller = new AbortController();
    setGroupData(null);
    setGroupError(null);
    setGroupLoading(true);
    if (!detail) return () => controller.abort();
    const targetOrgId = detail.org.id;
    const query = `?orgId=${encodeURIComponent(targetOrgId)}`;
    void Promise.all([
      fetch(`${API_BASE}/api/device-groups${query}`, { credentials: 'include', signal: controller.signal }),
      fetch(`${API_BASE}/api/device-groups/member-assignments${query}`, { credentials: 'include', signal: controller.signal }),
    ]).then(async (responses) => {
      const bodies = await Promise.all(responses.map(async (response) => {
        const body = await response.json();
        if (!response.ok) throw new Error(body.error || `그룹 조회 실패 (HTTP ${response.status})`);
        return body;
      }));
      if (controller.signal.aborted) return;
      const options = bodies[0] as GroupMemberOptions;
      if (options.actor.orgId !== targetOrgId) throw new Error('조직 정보를 다시 확인해 주세요.');
      setGroupData({ ...options, orgId: targetOrgId, members: bodies[1] as GroupMemberAssignments[] });
    }).catch((error: unknown) => {
      if (!controller.signal.aborted) setGroupError(error instanceof Error ? error.message : String(error));
    }).finally(() => {
      if (!controller.signal.aborted) setGroupLoading(false);
    });
    return () => controller.abort();
  }, [detail, groupRevision]);

  const groupMembers = useMemo(() => new Map(groupData?.members.map((member) => [member.userId, member])), [groupData]);
  const groupNames = useMemo(() => new Map(groupData?.groups.map((group) => [group.id, group.name])), [groupData]);
  const groupsReady = !groupLoading && groupData?.orgId === detail?.org.id;
  const canAssignGroups = groupsReady && groupData?.actor.central && groupData.actor.role === 'Owner';
  const groupLabel = (member: AdminRow): string => {
    if (member.role !== 'Owner' && !groupsReady) return groupError ? '조회 실패' : '불러오는 중…';
    const current = groupMembers.get(member.userId);
    // 그룹 이름만 적는다. 그룹별 역할은 더 이상 따로 고르지 않으므로(2026-09-17 대교) 여기에
    // '(편집자)' 같은 꼬리표를 붙이면 '권한' 칸과 두 번 말하는 셈이고, 옛 데이터의 낡은 역할이
    // 그대로 노출돼 지금 권한과 어긋나 보인다.
    return describeGroupScope({ role: member.role, central: current?.central },
      current?.assignments.map((assignment) => groupNames.get(assignment.groupId) ?? '조회 불가 그룹') ?? []);
  };

  const admins = detail?.admins ?? [];
  // 삭제 대기 조직 상세는 Only Read — 조직 수정·관리자 추가/수정/삭제 미제공.
  const orgDeleted = !!detail?.org.deletedAt;
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return admins;
    return admins.filter(
      (a) =>
        (a.name ?? '').toLowerCase().includes(q) ||
        (a.loginId ?? '').toLowerCase().includes(q) ||
        a.email.toLowerCase().includes(q),
    );
  }, [admins, query]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const rows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  if (isLoading) {
    return <OrgAdminDetailSkeleton />;
  }

  return (
    // relative — 안쪽의 sr-only(position:absolute) 글자가 위치 기준 조상 없이 문서 끝까지 뻗어
    // 화면 전체에 빈 스크롤이 생겼다(2026-10-06 대교). 스크롤 루트를 기준으로 잡아 안에서 잘리게 한다.
    <div className={`relative h-full overflow-auto ${SECTION_BODY_GUTTER}`}>
      {err && <p className="font-mono text-[12px] text-red mb-3">{err}</p>}

      {/* 삭제 예정 안내 배너 */}
      {detail?.org.deletedAt && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-blue/30 bg-blue-soft px-4 py-3 text-[13px] text-blue">
          <span aria-hidden>ⓘ</span>
          삭제 예정인 조직입니다. 삭제까지 {purgeDday(detail.org.purgeAt)}일 남았습니다.
        </div>
      )}

      {/* ── 조직 정보 ── */}
      <div className="border border-line rounded-md bg-panel mb-6">
        <div className="flex items-center justify-between px-5 py-3 border-b border-line max-md:flex-wrap max-md:gap-2 max-md:px-4">
          <h2 className="flex items-center gap-2 whitespace-nowrap text-[15px] font-semibold">
            조직 정보
            {detail?.org.deletedAt && (
              <span className="rounded-sm bg-red/10 px-2 py-0.5 text-[12px] font-medium text-red border border-red/30">
                삭제 예정 (D-{purgeDday(detail.org.purgeAt)})
              </span>
            )}
          </h2>
          <div className="flex gap-2 max-md:flex-wrap [&_button]:whitespace-nowrap">
            {/* 조직 삭제는 BO 전용. FO(self)는 자기 조직 삭제 불가. */}
            {scope === 'bo' && (
              detail?.org.deletedAt ? (
                <>
                  {/* 삭제 예정 상태: 즉시 삭제(복구 불가) + 복구 */}
                  <Button variant="danger" onClick={() => setPurgeOpen(true)} disabled={detail?.isBoOrg ?? true}>
                    삭제
                  </Button>
                  <Button variant="primary" onClick={() => setRestoreOpen(true)}>복구</Button>
                </>
              ) : (
                <Button variant="danger" onClick={() => setDeleteOpen(true)} disabled={detail?.isBoOrg ?? true}>
                  삭제
                </Button>
              )
            )}
            {/* 삭제 대기 조직은 수정 불가 — 수정 버튼 숨김. */}
            {!detail?.org.deletedAt && (
              <>
                {/* 고객사로 보기(2026-09-15 A안) — 이 고객사의 플레이리스트·스케줄·구성 관리·관리이력을 고객 화면 그대로, 읽기 전용으로. */}
                {/* 운영사 자신의 조직(isBoOrg)에도 보인다 — 숨기면 "버튼이 어디 있냐"가 된다(2026-09-15 대교). 자기 조직은 읽기 전용으로 보는 셈. */}
                {scope === 'bo' && (
                  <Button onClick={() => { void enterTenant(orgId, 'read').catch((e: unknown) => toast(describeApiError(e), 'error')); }}
                    title="이 고객사의 화면을 읽기 전용으로 봅니다. 상단 띠의 '돌아가기'로 돌아옵니다.">이 고객사로 보기</Button>
                )}
                {/* 디자인 설정은 운영사 전용 — 조직 Owner 화면에서는 뺐다(2026-09-16 대교). */}
                {scope === 'bo' && (
                  <Button onClick={() => navigate(`/h9/tenants/${encodeURIComponent(orgId)}/design`)}>디자인 설정</Button>
                )}
                <Button variant="primary" onClick={() => setRenameOpen(true)}>수정</Button>
              </>
            )}
          </div>
        </div>
        {detail && (
          <div className="px-5 py-2 divide-y divide-line">
            <div>
              <InfoBlock label="조직명" value={detail.org.name} />
              <InfoBlock label="조직 설정 등급" value={detail.org.orgRole === 'super' ? '최고 관리자 조직' : '관리자 조직'} />
              <p className="pb-3 text-[12px] text-text-dim">조직 등급과 사용자 권한은 별개입니다. 조직 전체 관리 권한은 아래 계정의 Owner 역할로 확인하세요.</p>
              <InfoBlock label="등록된 디바이스 수" value={String(detail.deviceCount)} />
            </div>
            <div>
              <InfoBlock label="최초 등록일" value={formatDateTime(detail.org.createdAt)} />
              {/* ponytail: 조직 수정 시각은 orgs에 updated_at이 생기면 표시. */}
              <InfoBlock label="마지막 수정일" value="-" />
            </div>
          </div>
        )}
      </div>

      {scope === 'bo' && detail && <OrgPlayerServerCard key={detail.org.id} orgId={detail.org.id} />}
      {scope === 'bo' && detail && <OrgAiConfigCard key={`ai-${detail.org.id}`} orgId={detail.org.id} />}

      {/* ── 관리자 정보 ── */}
      <div className="border border-line rounded-md bg-panel">
        <div className="px-5 py-3 border-b border-line">
          <h2 className="text-[15px] font-semibold">관리자 정보</h2>
        </div>
        <div className="px-5 py-4">
          {groupError && <div role="alert" className="mb-3 flex flex-wrap items-center gap-2 text-[13px] text-red">
            담당 그룹 조회 실패: {groupError}
            <Button disabled={groupLoading} onClick={() => setGroupRevision((value) => value + 1)}>다시 불러오기</Button>
          </div>}
          <p className="mb-3 text-[13px] text-text-dim">새 계정은 ‘관리자 추가’ 안에서 담당 그룹까지 함께 정합니다. 나중에 바꿀 때는 해당 행의 ‘담당 그룹’에서 하면 되고, 그룹 안에서의 권한은 ‘권한’ 칸 그대로입니다. 조직 관리자(Owner)는 항상 전체 그룹을 관리합니다.</p>
          {detail && !orgDeleted && (detail.ownerReadiness ? !detail.ownerReadiness.ready : !detail.admins.some(member => member.role === 'Owner')) && <p role="status" className="mb-3 rounded border border-line bg-panel-2 p-3 text-[13px] text-text-dim">
            {detail.ownerReadiness ? detail.ownerReadiness.reasons.map(reason => OWNER_ISSUES[reason]).join(' ') : '이 조직에 Owner 계정이 지정되지 않았습니다.'} 조직 등급을 바꾸는 것만으로 계정 권한이 부여되지는 않습니다.
          </p>}
          <div className="flex items-center justify-between mb-3 max-md:flex-wrap max-md:gap-2">
            <span className="whitespace-nowrap text-[14px] text-text-dim">총 {filtered.length}건</span>
            <div className="flex items-center gap-2 max-md:contents [&_button]:whitespace-nowrap">
              {!orgDeleted && scope === 'bo' && (
                <Button
                  title="기준 조직의 기본 템플릿 슬라이드(와 참조 미디어)를 이 조직으로 복제한다. 이미 있는 이름은 건너뛴다."
                  onClick={() =>
                    fetch(`${API_BASE}/api/bo/tenants/${encodeURIComponent(orgId)}/seed-templates`, { method: 'POST' })
                      .then(async (r) => {
                        if (!r.ok) throw new Error(`HTTP ${r.status}`);
                        const j = (await r.json()) as { notices: number; media: number; skipped: number };
                        toast(`기본 템플릿 ${j.notices}개·미디어 ${j.media}개 추가 (건너뜀 ${j.skipped})`, 'success');
                      })
                      .catch((e) => toast(describeApiError(e), 'error'))
                  }
                >
                  기본 템플릿 가져오기
                </Button>
              )}
              {!orgDeleted && (
                <Button variant="primary" onClick={() => { setNewAdminGroups(new Set()); setNewAdminCentral(false); setAddOpen(true); }}>+ 관리자 추가</Button>
              )}
              <label className="contents"><span className="sr-only">관리자명, 아이디 검색</span>
                <SearchInput
                  value={query}
                  onChange={(v) => {
                    setQuery(v);
                    setPage(1);
                  }}
                  placeholder="관리자명, 아이디 검색"
                  className="w-[220px] max-md:order-last max-md:w-full"
                />
              </label>
            </div>
          </div>
          <div className="border border-line rounded-md overflow-x-auto">
            <table className="w-full text-[14px]">
              <thead>
                <tr className="bg-panel-2 text-text-dim text-left">
                  <th className="px-3 py-2.5 font-medium">관리자명</th>
                  <th className="px-3 py-2.5 font-medium">권한</th>
                  <th className="px-3 py-2.5 font-medium">담당 그룹</th>
                  <th className="px-3 py-2.5 font-medium">아이디</th>
                  <th className="px-3 py-2.5 font-medium">연락처</th>
                  <th className="px-3 py-2.5 font-medium">이메일</th>
                  <th className="px-3 py-2.5 font-medium">마지막 접속일</th>
                  <th className="px-3 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.userId} className="border-t border-line hover:bg-panel-hover">
                    <td className="px-3 py-2.5">{a.name ?? '-'}</td>
                    <td className="px-3 py-2.5">{roleLabel(a.role)}</td>
                    <td className="max-w-[240px] px-3 py-2.5 text-[13px] text-text-dim">{groupLabel(a)}</td>
                    <td className="px-3 py-2.5">{a.loginId ?? '-'}</td>
                    <td className="px-3 py-2.5">{a.phone ?? '-'}</td>
                    <td className="px-3 py-2.5">{a.email}</td>
                    <td className="px-3 py-2.5">{formatDate(a.lastSeenAt)}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-right">
                      {!orgDeleted && (
                        <>
                          {a.role !== 'Owner' && (
                            <button type="button" disabled={!canAssignGroups}
                              aria-label={`${a.name ?? a.loginId ?? '계정'} 담당 그룹 편집`}
                              onClick={() => setGroupTarget(a)}
                              className="mr-1.5 inline-flex items-center rounded border border-line px-3 py-1.5 text-[13px] hover:border-line-strong hover:bg-panel disabled:cursor-not-allowed disabled:opacity-40">
                              담당 그룹
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => setEditTarget(a)}
                            className="inline-flex items-center px-3 py-1.5 mr-1.5 text-[13px] border border-line rounded hover:border-line-strong hover:bg-panel"
                          >
                            수정
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteAdminTarget(a)}
                            className="inline-flex items-center px-3 py-1.5 text-[13px] border border-line rounded hover:border-line-strong hover:bg-panel"
                          >
                            삭제
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-3 py-10 text-center text-text-dim">
                      조회된 관리자가 없습니다.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {pageCount > 1 && (
            <div className="flex justify-end items-center gap-1 mt-3">
              {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  type="button"
                  disabled={n === safePage}
                  onClick={() => setPage(n)}
                  className={`min-w-[28px] h-[28px] px-1 text-[13px] rounded border ${
                    n === safePage
                      ? 'border-accent text-accent font-semibold'
                      : 'border-line text-text-dim hover:text-text hover:border-line-strong'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── 모달들 ── */}
      {groupTarget && detail && !orgDeleted && (
        <GroupMemberEditor key={`${detail.org.id}:${groupTarget.userId}`} orgId={detail.org.id} member={groupTarget}
          onClose={() => setGroupTarget(null)} onSaved={() => {
            setGroupTarget(null);
            setGroupRevision((value) => value + 1);
            toast('담당 그룹이 저장되었습니다.', 'success');
          }} />
      )}
      {renameOpen && detail && (
        <OrgFormModal
          mode="edit"
          scope={scope}
          org={{ id: detail.org.id, name: detail.org.name, orgRole: detail.org.orgRole }}
          onClose={() => setRenameOpen(false)}
          onSaved={() => {
            setRenameOpen(false);
            toast('저장되었습니다.', 'success');
            load();
          }}
        />
      )}
      {deleteOpen && detail && (
        <ConfirmModal
          title="알림"
          message={'삭제 후 7일간 보관되며, 그 전까지 복구할 수 있습니다.\n7일이 지나면 조직·디바이스·미디어·관리자 계정이 완전히 삭제됩니다.\n삭제하시겠습니까?'}
          confirmLabel="확인"
          danger={false}
          onCancel={() => setDeleteOpen(false)}
          onConfirm={async () => {
            const r = await fetch(`${API_BASE}/api/bo/tenants/${encodeURIComponent(orgId)}`, {
              method: 'DELETE',
            });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            toast('삭제 예정으로 처리되었습니다. (7일 보관)', 'success');
            navigate('/h9/tenants');
          }}
        />
      )}
      {restoreOpen && detail && (
        <ConfirmModal
          title="알림"
          message={'삭제 예정을 취소하고 조직을 복구합니다.\n복구하시겠습니까?'}
          confirmLabel="복구"
          danger={false}
          onCancel={() => setRestoreOpen(false)}
          onConfirm={async () => {
            const r = await fetch(`${API_BASE}/api/bo/tenants/${encodeURIComponent(orgId)}/restore`, {
              method: 'POST',
            });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            toast('복구되었습니다.', 'success');
            setRestoreOpen(false);
            load();
          }}
        />
      )}
      {purgeOpen && detail && (
        <ConfirmModal
          title="알림"
          message={'조직과 하위 정보가 즉시 삭제되며 복구할 수 없습니다.\n삭제하시겠습니까?'}
          confirmLabel="확인"
          danger={false}
          onCancel={() => setPurgeOpen(false)}
          onConfirm={async () => {
            const r = await fetch(`${API_BASE}/api/bo/tenants/${encodeURIComponent(orgId)}/purge`, {
              method: 'POST',
            });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            toast('삭제되었습니다.', 'success');
            navigate('/h9/tenants');
          }}
        />
      )}
      {addOpen && detail && (
        <AdminFormModal
          mode="add"
          adminsBase={adminsBase}
          orgName={detail.org.name}
          access={({ role }) => adminAccessFields({ role, central: newAdminCentral, picked: newAdminGroups })}
          extra={({ role }) => (
            <AdminGroupSection role={role} mode="add" groups={groupData?.groups ?? []}
              picked={newAdminGroups} central={newAdminCentral}
              onPick={setNewAdminGroups} onCentral={setNewAdminCentral}
              {...(groupsReady ? {} : { note: groupError ? `담당 그룹을 불러오지 못했습니다: ${groupError}` : '담당 그룹을 불러오는 중입니다…' })} />
          )}
          onClose={() => setAddOpen(false)}
          onSaved={(result) => {
            setAddOpen(false);
            reportAdminSaveResult(result, '추가되었습니다.');
            setGroupRevision((value) => value + 1);
            load();
          }}
        />
      )}
      {editTarget && detail && (
        <AdminFormModal
          mode="edit"
          adminsBase={adminsBase}
          orgName={detail.org.name}
          admin={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={(result) => {
            setEditTarget(null);
            reportAdminSaveResult(result, '저장되었습니다.');
            load();
          }}
        />
      )}
      {deleteAdminTarget && (
        <ConfirmModal
          title="알림"
          message={'삭제한 내용은 복구할 수 없습니다.\n삭제하시겠습니까?'}
          confirmLabel="확인"
          danger={false}
          onCancel={() => setDeleteAdminTarget(null)}
          onConfirm={async () => {
            const r = await fetch(`${adminsBase}/${encodeURIComponent(deleteAdminTarget.userId)}`, {
              method: 'DELETE',
            });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            toast('삭제되었습니다.', 'success');
            load();
          }}
        />
      )}
    </div>
  );
}

function InfoBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="py-3">
      <div className="text-[13px] font-semibold text-text mb-1">{label}</div>
      <div className="text-[14px] text-text-dim">{value}</div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// 관리자 추가/수정 팝업 (Figma Popup_관리자 추가 / 관리자 수정)
// -----------------------------------------------------------------------------
const PASSWORD_RE = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{10,}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** SCDS-73 — 나중에 정하는 아이디(서버 LOGIN_ID_SET_RE 와 같다). '@'가 있으면 로그인이 이메일로 읽는다. */
const LOGIN_ID_SET_RE = /^[A-Za-z0-9._-]{3,40}$/;

// 조직 내 멤버 역할(RBAC). 명칭은 labels/orgConcepts.ts 한 곳에서 관리한다.
const MEMBER_ROLES = ROLE_OPTIONS;
type MemberRoleValue = (typeof MEMBER_ROLES)[number]['value'];
const roleLabel = (r: string) => MEMBER_ROLES.find((m) => m.value === r)?.label ?? r;

export function AdminFormModal({
  mode,
  adminsBase,
  orgName,
  admin,
  onClose,
  onSaved,
  extra,
  access,
  dangerZone,
}: {
  mode: 'add' | 'edit';
  /** 관리자 CRUD 엔드포인트 base — BO(tenants/:id/admins) 또는 FO(orgs/current/admins). */
  adminsBase: string;
  orgName: string;
  admin?: AdminRow;
  onClose: () => void;
  /** 서버가 돌려준 저장 결과. 호출자가 userId·배정·발송 결과를 그대로 쓴다(추론 금지). */
  onSaved: (result: AdminSaveResult) => void;
  /** 호출자가 끼워 넣는 추가 섹션(예: 구성 관리의 '담당 그룹'). 폼 필드 아래, 버튼 위에 그린다.
   *  함수를 주면 지금 고른 권한(role)을 받아 그린다 — 권한에 따라 켜지고 꺼지는 옵션(중앙 관리자 등)용. */
  extra?: ReactNode | ((ctx: { role: string }) => ReactNode);
  /** 같은 요청에 함께 저장할 접근 권한. 지금 고른 권한을 받아 만든다.
   *  주지 않으면 서버가 기존 배정을 보존한다(레거시 호출부). */
  access?: (ctx: { role: string }) => { central?: boolean; assignments?: Array<{ groupId: string; role: string }> };
  /** 본문 맨 아래 '위험 구역'. 삭제처럼 되돌릴 수 없는 동작을 여기 둔다 — 목록의 행 옆이
   *  아니라 수정 화면 끝에 둬야 무엇을 지우는지 보고 누른다(2026-09-18 대교). */
  dangerZone?: ReactNode;
}) {
  const [name, setName] = useState(admin?.name ?? '');
  const [loginId, setLoginId] = useState(admin?.loginId ?? '');
  const [password, setPassword] = useState('');
  // 수정 모드: 비밀번호 초기화 버튼을 누르기 전엔 마스킹 표시만.
  const [pwReset, setPwReset] = useState(mode === 'add');
  const [p1, setP1] = useState(admin?.phone?.split('-')[0] ?? '');
  const [p2, setP2] = useState(admin?.phone?.split('-')[1] ?? '');
  const [p3, setP3] = useState(admin?.phone?.split('-')[2] ?? '');
  const [email, setEmail] = useState(admin?.email ?? '');
  const [role, setRole] = useState<MemberRoleValue>(
    (MEMBER_ROLES.some((m) => m.value === admin?.role) ? admin!.role : 'Admin') as MemberRoleValue,
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  // BRICK-180 — 이미 있는 계정을 이 조직에 연결할지 확인받는 단계.
  const [existingUser, setExistingUser] = useState<{
    email: string;
    name: string | null;
    loginId: string | null;
    /** 아이디가 겹쳤는지 이메일이 겹쳤는지 — 무엇을 바꿔야 새 계정을 만들 수 있는지 알려준다. */
    conflict?: 'loginId' | 'email';
    /** 이 조직에서 이미 가진 권한(없으면 null). */
    currentRole?: string | null;
    /** 연결하면 실제로 적용될 권한. 고른 권한과 이미 가진 권한 중 높은 쪽. */
    resultingRole?: string;
    /** SCDS-73 — 비밀번호 로그인 정보가 있는가. 없으면 첫 비밀번호를 정해야 로그인된다. */
    hasPassword?: boolean;
    /** 이 화면에서 첫 비밀번호를 정할 수 있는가(운영사 계정은 운영사만). */
    canSetPassword?: boolean;
  } | null>(null);
  const [linkConfirm, setLinkConfirm] = useState(false);
  // SCDS-73 — 이메일을 적자마자 기존 계정인지 확인한다. 예전엔 이메일이 맨 아래 칸이라
  // 아이디·비밀번호·연락처를 다 채운 뒤에야 '이미 있는 계정'임을 알았고, 그 입력은 버려졌다.
  type ExistingAccount = NonNullable<typeof existingUser>;
  const [linkable, setLinkable] = useState<(ExistingAccount & { lookup: string }) | null>(null);
  const normalizedEmail = email.trim().toLowerCase();
  const linking = mode === 'add' && !!linkable && linkable.lookup === normalizedEmail;
  // SCDS-73 — 연결할 계정에 비어 있는 것만 여기서 채운다. 아이디가 없으면 아이디를, 비밀번호 로그인
  // 정보가 없으면 첫 비밀번호를 받는다. 예전에는 연결만 되고 아이디가 비어 로그인할 수 없었다.
  const [linkLoginId, setLinkLoginId] = useState('');
  const [linkPassword, setLinkPassword] = useState('');
  const linkNeedsLoginId = linking && !linkable!.loginId;
  const linkNeedsPassword = linking && linkable!.hasPassword === false && linkable!.canSetPassword === true;
  const linkPasswordBlocked = linking && linkable!.hasPassword === false && linkable!.canSetPassword !== true;
  // 수정 창 — 아이디가 비어 있는 계정만 한 번 정할 수 있다(정한 뒤에는 바꿀 수 없다).
  const loginIdEditable = mode === 'add' || !admin?.loginId;
  // 입력마다 라벨을 htmlFor로 묶는다(검수 S16 — 예전엔 label도 aria-label도 없었다).
  const ids = useId();
  // 대행 '읽기 전용'에서도 폼은 그대로 두고 저장만 막는다(검수 S1).
  const write = useWriteGate();

  // 확인을 누르면 같은 폼을 linkExisting=true 로 한 번 더 보낸다.
  useEffect(() => {
    if (!linkConfirm) return;
    void submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkConfirm]);

  const phone = p1 && p2 && p3 ? `${p1}-${p2}-${p3}` : '';
  const lookupExisting = async (): Promise<(ExistingAccount & { lookup: string }) | null> => {
    const lookup = normalizedEmail;
    if (mode !== 'add' || !EMAIL_RE.test(lookup)) return null;
    try {
      const r = await fetch(adminsBase, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: lookup, role, ...(access?.({ role }) ?? {}), checkOnly: true }),
      });
      const body = (await r.json().catch(() => ({}))) as {
        error?: string; user?: { email: string; name: string | null; loginId: string | null };
        currentRole?: string | null; resultingRole?: string; hasPassword?: boolean; canSetPassword?: boolean;
      };
      if (r.status !== 409 || body.error !== 'existing_user' || !body.user) return null;
      return { ...body.user, conflict: 'email', currentRole: body.currentRole ?? null,
        ...(body.resultingRole ? { resultingRole: body.resultingRole } : {}),
        ...(body.hasPassword !== undefined ? { hasPassword: body.hasPassword } : {}),
        ...(body.canSetPassword !== undefined ? { canSetPassword: body.canSetPassword } : {}), lookup };
    } catch {
      return null;
    }
  };
  const checkEmail = async () => {
    if (mode !== 'add' || linkable?.lookup === normalizedEmail) return;
    setLinkable(await lookupExisting());
  };

  const valid = linking
    ? (!linkNeedsLoginId || linkLoginId.trim().length > 0) && (!linkNeedsPassword || linkPassword.length > 0)
    :
    name.trim().length > 0 &&
    (mode === 'edit' || loginId.trim().length > 0) &&
    (!pwReset || password.length > 0 || mode === 'edit') &&
    (mode === 'add' ? password.length > 0 && phone && email.trim().length > 0 : true);

  const digits = (v: string, max: number) => v.replace(/\D/g, '').slice(0, max);

  const submit = async () => {
    if (!valid || busy || write.blocked) return;
    // 기존 계정 연결 — 새 계정 칸은 검사하지 않는다. 고른 권한으로 한 번 더 확인해 연결 모달을 띄운다.
    if (linking && !linkConfirm) {
      setBusy(true);
      const found = await lookupExisting();
      setBusy(false);
      if (found) setExistingUser(found); else setLinkable(null);
      return;
    }
    // 클라이언트 검증(Figma Error Case).
    const errs: Record<string, string> = {};
    if (linking && linkNeedsLoginId && !LOGIN_ID_SET_RE.test(linkLoginId.trim())) {
      errs['linkLoginId'] = '아이디는 영문·숫자·. _ - 조합 3~40자로 입력해 주세요.';
    }
    if (linking && linkNeedsPassword && (!PASSWORD_RE.test(linkPassword) || new TextEncoder().encode(linkPassword).length > 72)) {
      errs['linkPassword'] = '비밀번호는 영문·숫자·특수문자 조합 10자 이상, UTF-8 72바이트 이하여야 합니다.';
    }
    if (mode === 'edit' && !admin?.loginId && loginId.trim() && !LOGIN_ID_SET_RE.test(loginId.trim())) {
      errs['loginId'] = '아이디는 영문·숫자·. _ - 조합 3~40자로 입력해 주세요.';
    }
    if ((mode === 'add' || pwReset) && password && (!PASSWORD_RE.test(password) || new TextEncoder().encode(password).length > 72)) {
      errs['password'] = '비밀번호는 영문·숫자·특수문자 조합 10자 이상, UTF-8 72바이트 이하여야 합니다.';
    }
    if (mode === 'add' && !linking) {
      if (!(p1.length === 3 && p2.length >= 3 && p3.length === 4)) {
        errs['phone'] = '형식이 올바르지 않습니다.';
      }
      if (!EMAIL_RE.test(email.trim())) {
        errs['email'] = '형식이 올바르지 않습니다.';
      }
    }
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      // 권한·중앙 여부·담당 그룹을 한 요청에 함께 보낸다. 예전에는 저장 뒤 목록을
      // 다시 읽어 '새로 생긴 사람'을 추론하고 배정을 따로 저장해서, 중간에 실패하면
      // 계정만 만들어지고 담당 그룹이 비는 부분 저장이 났다(R07).
      const accessFields = access?.({ role }) ?? {};
      if (mode === 'add') {
        const r = await fetch(adminsBase, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          // 기존 계정 연결은 이메일로만 찾는다 — 적어 둔 아이디가 다른 계정과 겹쳐 엉뚱한 사람이 붙지 않게.
          body: JSON.stringify({
            ...(linking ? {
              email: normalizedEmail,
              ...(linkNeedsLoginId ? { setLoginId: linkLoginId.trim() } : {}),
              ...(linkNeedsPassword ? { setPassword: linkPassword } : {}),
            } : {
              name: name.trim(),
              loginId: loginId.trim(),
              password,
              phone,
              email: email.trim(),
            }),
            role,
            ...accessFields,
            // BRICK-180 — 두 번째 호출에서만 true. 기존 계정을 이 조직에 붙인다.
            ...(linkConfirm ? { linkExisting: true } : {}),
          }),
        });
        if (!r.ok) {
          const body = (await r.json().catch(() => ({}))) as {
            error?: string;
            detail?: string;
            user?: { email: string; name: string | null; loginId: string | null };
            currentRole?: string | null;
            conflict?: 'loginId' | 'email';
            resultingRole?: string;
          };
          if (body.error === 'existing_user' && body.user) {
            // 이미 있는 계정 — 새로 만들지 말고 이 조직에 연결할지 묻는다.
            setExistingUser({
              ...body.user,
              ...(body.conflict ? { conflict: body.conflict } : {}),
              currentRole: body.currentRole ?? null,
              ...(body.resultingRole ? { resultingRole: body.resultingRole } : {}),
            });
            setBusy(false);
            return;
          }
          if (body.error === 'duplicate_login_id') {
            setErrors(linking ? { linkLoginId: '중복된 아이디입니다.' } : { loginId: '중복된 아이디입니다.' });
          } else if (body.error === 'invalid_login_id' || body.error === 'login_id_already_set') {
            setErrors({ [linking ? 'linkLoginId' : 'loginId']: body.detail ?? '아이디를 확인해 주세요.' });
          } else if (linking && body.error === 'invalid_password') {
            setErrors({ linkPassword: '비밀번호는 영문·숫자·특수문자 조합 10자 이상, UTF-8 72바이트 이하여야 합니다.' });
          } else if (body.error === 'duplicate_email') {
            setErrors({ email: '이미 사용 중인 이메일입니다.' });
          } else if (body.error === 'invalid_password') {
            setErrors({ password: '비밀번호는 영문·숫자·특수문자 조합 10자 이상, UTF-8 72바이트 이하여야 합니다.' });
          } else {
            // 서버가 사용자용 사유를 주면 그대로 보인다. 'HTTP 409' 원문을 띄우지 않는다(R18).
            setErrors({ form: body.detail ?? describeApiError(new Error(`HTTP ${r.status}`)) });
          }
          setBusy(false);
          return;
        }
        onSaved((await r.json().catch(() => ({}))) as AdminSaveResult);
        return;
      } else {
        const r = await fetch(
          `${adminsBase}/${encodeURIComponent(admin!.userId)}`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: name.trim(),
              phone: phone || null,
              role,
              ...accessFields,
              // BRICK-182 — 이메일 변경 지원.
              email: email.trim(),
              // SCDS-73 — 아이디가 비어 있던 계정만 여기서 한 번 정한다.
              ...(!admin?.loginId && loginId.trim() ? { loginId: loginId.trim() } : {}),
              ...(pwReset && password ? { password } : {}),
            }),
          },
        );
        if (!r.ok) {
          const body = (await r.json().catch(() => ({}))) as { error?: string; detail?: string };
          if (body.error === 'invalid_password') {
            setErrors({ password: '비밀번호는 영문·숫자·특수문자 조합 10자 이상, UTF-8 72바이트 이하여야 합니다.' });
          } else if (body.error === 'duplicate_login_id') {
            setErrors({ loginId: '중복된 아이디입니다.' });
          } else if (body.error === 'invalid_login_id' || body.error === 'login_id_already_set') {
            setErrors({ loginId: body.detail ?? '아이디를 확인해 주세요.' });
          } else if (body.error === 'duplicate_email') {
            setErrors({ email: '이미 사용 중인 이메일입니다.' });
          } else if (body.error === 'invalid_email') {
            setErrors({ email: '형식이 올바르지 않습니다.' });
          } else {
            setErrors({ form: body.detail ?? describeApiError(new Error(`HTTP ${r.status}`)) });
          }
          setBusy(false);
          return;
        }
        onSaved((await r.json().catch(() => ({}))) as AdminSaveResult);
        return;
      }
    } catch (e) {
      setErrors({ form: describeApiError(e) });
      setBusy(false);
    }
  };

  const inputCls = (bad?: boolean) =>
    `w-full px-3 py-2 bg-panel-2 border rounded text-[14px] text-text ${bad ? 'border-red' : 'border-line'}`;
  const label = 'text-[13px] font-semibold text-text';
  const errText = 'text-[12px] text-red mt-1';
  // 추가 모드에서는 이메일을 맨 위(권한 아래)에 둔다 — 기존 계정이면 나머지 칸이 필요 없다(SCDS-73).
  const emailField = (
        <div>
          {/* BRICK-182 — 이메일은 수정 모드에서도 바꿀 수 있다(로그인 아이디는 불변). */}
          <label className={label} htmlFor={`${ids}-email`}>
            이메일 <span className="text-red">*</span>
          </label>
          <input
            id={`${ids}-email`}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => void checkEmail()}
            autoFocus={mode === 'add'}
            placeholder="이메일 입력"
            className={`${inputCls(!!errors['email'])} mt-1.5`}
          />
          {errors['email'] && <p className={errText}>{errors['email']}</p>}
          {linking && linkable && <p role="status" className="mt-1.5 rounded border border-blue/40 bg-blue-soft px-3 py-2 text-[13px] text-blue">
            이미 가입된 계정입니다 — {linkable.name ?? linkable.email}{linkable.loginId ? ` (아이디 ${linkable.loginId})` : ''}.<br />
            {linkable.currentRole ? <>이미 이 조직의 {roleLabel(linkable.currentRole)}입니다. </> : null}추가하면 새 계정을 만들지 않고 이 계정을 조직에 연결합니다. 관리자명·연락처는 기존 계정 정보를 그대로 씁니다.
            {linkNeedsLoginId ? ' 이 계정은 아이디가 없어 아래에서 아이디를 정해야 합니다.' : ''}
            {linkNeedsPassword ? ' 비밀번호도 없어 처음 쓸 비밀번호를 정해야 로그인할 수 있습니다.' : ''}
          </p>}
          {linkNeedsLoginId && (
            <div className="mt-3">
              <label className={label} htmlFor={`${ids}-link-login`}>아이디 <span className="text-red">*</span></label>
              <input id={`${ids}-link-login`} value={linkLoginId} onChange={(e) => setLinkLoginId(e.target.value)}
                placeholder="이 계정이 로그인할 아이디" className={`${inputCls(!!errors['linkLoginId'])} mt-1.5`} />
              {errors['linkLoginId'] ? <p className={errText}>{errors['linkLoginId']}</p>
                : <p className="mt-1 text-[12px] text-text-dim">한 번 정하면 바꿀 수 없습니다. 이 계정을 쓰는 모든 조직에서 같은 아이디로 로그인합니다.</p>}
            </div>
          )}
          {linkNeedsPassword && (
            <div className="mt-3">
              <label className={label} htmlFor={`${ids}-link-password`}>비밀번호 <span className="text-red">*</span></label>
              <input id={`${ids}-link-password`} type="password" value={linkPassword} onChange={(e) => setLinkPassword(e.target.value)}
                placeholder="영문·숫자·특수문자 포함 10자 이상" className={`${inputCls(!!errors['linkPassword'])} mt-1.5`} />
              {errors['linkPassword'] ? <p className={errText}>{errors['linkPassword']}</p>
                : <p className="mt-1 text-[12px] text-text-dim">안내 메일로 보내며, 처음 로그인할 때 새 비밀번호로 바꾸게 됩니다.</p>}
            </div>
          )}
          {linkPasswordBlocked && (
            <p role="status" className="mt-1.5 text-[12px] text-text-dim">
              이 계정은 비밀번호 로그인 정보가 없는 운영사 계정입니다. 로그인하려면 운영사 관리자에게 비밀번호 설정을 요청해 주세요.
            </p>
          )}
        </div>
  );

  // 폭은 ModalShell의 md(560) 한 단계로, 제출·취소는 고정 푸터로(검수 S3).
  // 예전엔 패널 자체가 스크롤이라 1280×800에서 '추가' 버튼이 열자마자 화면 밖에 있었다.
  return (
    <ModalShell title={mode === 'add' ? '관리자 추가' : '관리자 수정'} onClose={onClose} size="md"
      footer={<>
        <Button onClick={onClose} disabled={busy}>취소</Button>
        <Button variant="primary" {...write.props} onClick={() => void submit()} disabled={!valid || busy || write.blocked}>
          {busy ? '처리 중…' : mode === 'add' ? '추가' : '저장'}
        </Button>
      </>}>
      <div className="flex flex-col gap-4">
        <div>
          <label className={label} htmlFor={`${ids}-org`}>조직</label>
          <input id={`${ids}-org`} value={orgName} disabled className={`${inputCls()} mt-1.5 bg-panel-3 text-text-dim cursor-not-allowed`} />
        </div>
        <div>
          <span className={label} id={`${ids}-role-label`}>권한 <span className="text-red">*</span></span>
          <div className="mt-1.5 grid grid-cols-2 gap-2" role="group" aria-labelledby={`${ids}-role-label`}>
            {MEMBER_ROLES.map((m) => (
              <label
                key={m.value}
                className={`flex cursor-pointer items-start gap-2 rounded border px-3 py-2 ${role === m.value ? 'border-accent bg-accent-soft/40' : 'border-line'}`}
              >
                <input type="radio" name="member-role" value={m.value} checked={role === m.value} onChange={() => setRole(m.value)} className="mt-1" />
                <span>
                  <span className="block text-[14px] text-text">{m.label}</span>
                  <span className="block text-[12px] text-text-dim">{m.desc}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
        {mode === 'add' && emailField}
        {/* 기존 계정 연결 중이면 새 계정 칸은 쓰지 않는다 — 잠가서 헷갈리지 않게. */}
        <fieldset disabled={linking} className={`contents ${linking ? '[&_input]:opacity-50' : ''}`}>
        <div>
          <label className={label} htmlFor={`${ids}-name`}>관리자명 <span className="text-red">*</span></label>
          <input
            id={`${ids}-name`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="관리자명 입력"
            autoFocus={mode === 'edit'}
            className={`${inputCls()} mt-1.5`}
          />
        </div>
        <div>
          <label className={label} htmlFor={`${ids}-login`}>아이디 <span className="text-red">*</span></label>
          <input
            id={`${ids}-login`}
            value={loginId}
            onChange={(e) => setLoginId(e.target.value)}
            placeholder={loginIdEditable && mode === 'edit' ? '아이디가 없습니다 — 한 번 정할 수 있습니다' : '아이디 입력'}
            disabled={!loginIdEditable}
            className={`${inputCls(!!errors['loginId'])} mt-1.5 ${!loginIdEditable ? 'bg-panel-3 text-text-dim cursor-not-allowed' : ''}`}
          />
          {errors['loginId'] && <p className={errText}>{errors['loginId']}</p>}
          {!errors['loginId'] && mode === 'edit' && loginIdEditable && (
            <p className="mt-1 text-[12px] text-text-dim">다른 경로로 만들어진 계정이라 아이디가 비어 있습니다. 정하면 이 아이디로도 로그인할 수 있고, 이후에는 바꿀 수 없습니다.</p>
          )}
        </div>
        <div>
          <label className={label} htmlFor={`${ids}-password`}>비밀번호 {mode === 'add' && <span className="text-red">*</span>}</label>
          {!pwReset ? (
            <div className="flex items-center gap-3 mt-1.5">
              <span className="text-[14px] text-text-dim tracking-widest">*********</span>
              <Button onClick={() => setPwReset(true)}>비밀번호 초기화</Button>
            </div>
          ) : (
            <input
              id={`${ids}-password`}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="영문·숫자·특수문자 포함 10자 이상"
              className={`${inputCls(!!errors['password'])} mt-1.5`}
            />
          )}
          {errors['password'] && <p className={errText}>{errors['password']}</p>}
        </div>
        <div>
          {/* 연락처는 칸이 셋이라 각 칸에 어느 자리인지 따로 알려 준다(검수 S16). */}
          <span className={label} id={`${ids}-phone-label`}>연락처 {mode === 'add' && <span className="text-red">*</span>}</span>
          <div className="flex items-center gap-2 mt-1.5" role="group" aria-labelledby={`${ids}-phone-label`}>
            <input aria-label="연락처 앞자리" value={p1} onChange={(e) => setP1(digits(e.target.value, 3))} className={`${inputCls(!!errors['phone'])} w-[80px]`} />
            <span className="text-text-dim">-</span>
            <input aria-label="연락처 가운데자리" value={p2} onChange={(e) => setP2(digits(e.target.value, 4))} className={`${inputCls(!!errors['phone'])} w-[90px]`} />
            <span className="text-text-dim">-</span>
            <input aria-label="연락처 끝자리" value={p3} onChange={(e) => setP3(digits(e.target.value, 4))} className={`${inputCls(!!errors['phone'])} w-[90px]`} />
          </div>
          {errors['phone'] && <p className={errText}>{errors['phone']}</p>}
        </div>
        {mode === 'edit' && emailField}
        </fieldset>
        {typeof extra === 'function' ? extra({ role }) : extra}
        {dangerZone}
        {errors['form'] && <p className="font-mono text-[12px] text-red">{errors['form']}</p>}
      </div>

      {/* BRICK-180 — 다른 조직에 이미 있는 계정이면 연결할지 먼저 묻는다. */}
      {existingUser && (
        <ConfirmModal
          title="기존 계정 연결"
          message={
            // 무엇이 겹쳤는지, 무엇이 버려지는지, 어떤 권한으로 붙는지를 분명히 말한다.
            // 예전에는 "이미 등록된 계정입니다"만 있어서, 아이디를 바꿔 새 계정을 만들려던
            // 것인지 같은 사람을 붙이려던 것인지 판단할 단서가 없었다(2026-09-16 대교).
            (existingUser.conflict === 'loginId'
              ? `아이디 '${loginId.trim()}'는 이미 ${existingUser.name ?? existingUser.email} 계정이 쓰고 있습니다.\n`
              : existingUser.conflict === 'email'
                ? `이메일 '${email.trim()}'은 이미 ${existingUser.name ?? existingUser.email} 계정이 쓰고 있습니다.\n`
                : `이미 등록된 계정입니다 (${existingUser.name ?? existingUser.email}).\n`) +
            '새 계정을 만들지 않고 이 계정을 이 조직의 관리자로 추가합니다.\n' +
            `권한은 '${roleLabel(existingUser.resultingRole ?? role)}'로 붙습니다` +
            (existingUser.currentRole && existingUser.resultingRole !== role
              ? ` (이 조직에서 이미 '${roleLabel(existingUser.currentRole)}'이라 낮추지 않습니다).\n`
              : '.\n') +
            (linking ? '관리자명·연락처는 기존 계정 정보를 그대로 씁니다.\n'
              : '입력한 비밀번호·관리자명·연락처는 저장되지 않고 기존 계정 정보를 그대로 씁니다.\n') +
            '다른 사람이라면 취소하고 아이디와 이메일을 모두 바꿔 주세요.'
          }
          confirmLabel="연결"
          danger={false}
          onCancel={() => setExistingUser(null)}
          onConfirm={() => {
            setExistingUser(null);
            setLinkConfirm(true);
          }}
        />
      )}
    </ModalShell>
  );
}
